import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Download, Loader2, Lock, RefreshCw, Search, Dices } from "lucide-react";
import {
  adminEntries, adminLogin, adminStats, adminFormErrors,
  adminDraws, adminExecuteDraw, adminSetWinnerStatus, downloadCsv,
} from "@/lib/api";
import { ChickenAvatar } from "@/components/Mascot";

const WINNER_STATUSES = ["gezogen", "kontaktiert", "bestätigt", "ausbezahlt"];

const inputCls = "h-11 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink placeholder:text-mutedlight/50 focus:border-gold focus:outline-none";
const selectCls = "h-11 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink appearance-none pr-8 focus:border-gold focus:outline-none";

const KpiCard = ({ label, value, testId }) => (
  <div className="rounded-[16px] border border-line bg-white p-5" data-testid={testId}>
    <p className="font-display text-3xl font-extrabold tracking-tight text-ink">{value}</p>
    <p className="mt-1 text-xs font-medium text-mutedlight">{label}</p>
  </div>
);

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(null);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [formErrors, setFormErrors] = useState(null);
  const [draws, setDraws] = useState(null);
  const [search, setSearch] = useState("");
  const [consent, setConsent] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawBusy, setDrawBusy] = useState("");

  const loadEntries = useCallback(async (tok, params = {}) => {
    const { data } = await adminEntries(tok, params);
    setEntries(data.entries);
  }, []);

  const loadAll = useCallback(async (tok) => {
    const [s, e, fe, dr] = await Promise.all([
      adminStats(tok), adminEntries(tok), adminFormErrors(tok), adminDraws(tok),
    ]);
    setStats(s.data);
    setEntries(e.data.entries);
    setFormErrors(fe.data);
    setDraws(dr.data.draws);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await adminLogin(password);
      setToken(data.token);
      await loadAll(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => {
      loadEntries(token, { search: search || undefined, consent: consent || undefined }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search, consent, token, loadEntries]);

  const executeDraw = async (date) => {
    setDrawBusy(date);
    try {
      await adminExecuteDraw(token, date);
      const { data } = await adminDraws(token);
      setDraws(data.draws);
    } catch (err) {
      alert(err.response?.data?.detail || "Ziehung fehlgeschlagen.");
    } finally {
      setDrawBusy("");
    }
  };

  const setStatus = async (drawDate, entryId, status) => {
    try {
      await adminSetWinnerStatus(token, drawDate, entryId, status);
      const { data } = await adminDraws(token);
      setDraws(data.draws);
    } catch {
      alert("Status konnte nicht gespeichert werden.");
    }
  };

  const k = stats?.kpis || {};
  const c = stats?.conversion || {};

  return (
    <main className="min-h-screen bg-paper py-14" data-testid="admin-page">
      <div className="mx-auto max-w-[1100px] px-5">
        <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-mutedlight transition-colors duration-200 hover:text-gold-dark">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Zurück zur Landingpage
        </Link>
        <div className="mt-6 flex items-center gap-3">
          <ChickenAvatar className="h-8 w-8" />
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink">Admin</h1>
        </div>
        {!token ? (
          <form onSubmit={handleLogin} className="mt-8 max-w-[480px] rounded-[18px] border border-line bg-white p-8" data-testid="admin-login-form">
            <label htmlFor="admin-password" className="mb-2 block text-sm font-medium text-ink">Passwort</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-14 w-full rounded-xl border border-line bg-offwhite px-4 text-ink transition-[border-color] duration-200 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
              placeholder="Admin-Passwort"
              autoComplete="current-password"
            />
            {error && <p className="mt-3 text-sm text-red-600" role="alert" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-[14px] bg-gold font-display text-lg font-extrabold text-ink transition-[background-color,opacity] duration-200 hover:bg-gold-dark disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4" /> Anmelden</>}
            </button>
          </form>
        ) : (
          <div className="mt-8 space-y-6" data-testid="admin-dashboard">
            {stats && (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="admin-kpi-grid">
                  <KpiCard label="Seitenaufrufe" value={k.page_views ?? 0} testId="kpi-page-views" />
                  <KpiCard label="Formular gesehen" value={k.form_views ?? 0} testId="kpi-form-views" />
                  <KpiCard label="Formular begonnen" value={k.form_starts ?? 0} testId="kpi-form-starts" />
                  <KpiCard label="Submit-Versuche" value={k.submit_attempts ?? 0} testId="kpi-submit-attempts" />
                  <KpiCard label="Anmeldungen" value={k.registrations ?? 0} testId="kpi-registrations" />
                  <KpiCard label="Formularfehler" value={k.form_errors_total ?? 0} testId="kpi-form-errors" />
                  <KpiCard label="Bonus-E-Mails gesendet" value={k.bonus_emails_sent ?? 0} testId="kpi-bonus-sent" />
                  <KpiCard label="Bonus-E-Mails fehlgeschlagen" value={k.bonus_emails_failed ?? 0} testId="kpi-bonus-failed" />
                  <KpiCard label="Bonus-Downloads" value={k.bonus_downloads ?? 0} testId="kpi-bonus-downloads" />
                  <KpiCard label="Nächste Ziehung" value={k.next_draw?.label || "–"} testId="kpi-next-draw" />
                </div>
                <div className="rounded-[16px] border border-line bg-white p-6" data-testid="admin-conversion">
                  <h2 className="font-display text-lg font-extrabold text-ink">Conversion-Raten</h2>
                  <ul className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2 lg:grid-cols-5">
                    <li className="rounded-xl bg-offwhite px-4 py-3"><span className="text-mutedlight">Aufruf → Formularstart</span><br /><span className="font-bold text-ink">{c.view_to_start ?? 0} %</span></li>
                    <li className="rounded-xl bg-offwhite px-4 py-3"><span className="text-mutedlight">Formularstart → Submit</span><br /><span className="font-bold text-ink">{c.start_to_submit ?? 0} %</span></li>
                    <li className="rounded-xl bg-offwhite px-4 py-3"><span className="text-mutedlight">Submit → Erfolg</span><br /><span className="font-bold text-ink">{c.submit_to_success ?? 0} %</span></li>
                    <li className="rounded-xl bg-offwhite px-4 py-3"><span className="text-mutedlight">Aufruf → Lead</span><br /><span className="font-bold text-ink">{c.view_to_lead ?? 0} %</span></li>
                    <li className="rounded-xl bg-offwhite px-4 py-3"><span className="text-mutedlight">Lead → Bonusdownload</span><br /><span className="font-bold text-ink">{c.lead_to_download ?? 0} %</span></li>
                  </ul>
                </div>
              </>
            )}

            {draws && (
              <div className="rounded-[16px] border border-line bg-white p-6" data-testid="admin-draws-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-lg font-extrabold text-ink">Gewinnerziehungen</h2>
                  <button
                    data-testid="admin-draws-csv-button"
                    onClick={() => downloadCsv(token, "draws")}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-line px-4 text-sm font-medium text-ink transition-colors hover:border-gold"
                  >
                    <Download className="h-4 w-4" /> Ziehungen (CSV)
                  </button>
                </div>
                <div className="mt-4 space-y-4">
                  {draws.map((d) => (
                    <div key={d.date} className="rounded-[14px] border border-line bg-offwhite p-5" data-testid={`draw-${d.date}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-display text-base font-extrabold text-ink">{d.label}</p>
                          <p className="text-xs text-mutedlight">
                            {d.prizes.map((p) => `${p.count}× ${p.label}`).join(" · ")}
                          </p>
                        </div>
                        {d.executed ? (
                          <span className="rounded-full bg-gold-dim px-3 py-1 text-xs font-bold text-gold-dark" data-testid={`draw-${d.date}-executed`}>
                            Gezogen · {d.eligible_count} Teilnehmer
                          </span>
                        ) : (
                          <button
                            data-testid={`draw-${d.date}-execute-button`}
                            disabled={drawBusy === d.date}
                            onClick={() => executeDraw(d.date)}
                            className="inline-flex h-10 items-center gap-2 rounded-xl bg-gold px-4 text-sm font-bold text-ink transition-colors hover:bg-gold-dark disabled:opacity-60"
                          >
                            {drawBusy === d.date ? <Loader2 className="h-4 w-4 animate-spin" /> : <Dices className="h-4 w-4" />}
                            Ziehung durchführen
                          </button>
                        )}
                      </div>
                      {d.executed && d.winners.length > 0 && (
                        <div className="mt-4 overflow-x-auto">
                          <table className="w-full min-w-[640px] text-left text-sm">
                            <thead>
                              <tr className="border-b border-line text-xs uppercase tracking-wider text-mutedlight">
                                <th className="py-2 pr-3">Preis</th>
                                <th className="py-2 pr-3">Gewinner</th>
                                <th className="py-2 pr-3">Kontakt</th>
                                <th className="py-2 pr-3">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.winners.map((w) => (
                                <tr key={w.entry_id} className="border-b border-line/60" data-testid="draw-winner-row">
                                  <td className="py-2.5 pr-3 font-medium text-ink">{w.prize_label}</td>
                                  <td className="py-2.5 pr-3 text-coal">{w.first_name} {w.last_name}</td>
                                  <td className="py-2.5 pr-3 text-coal">{w.email}<br />{w.phone}</td>
                                  <td className="py-2.5 pr-3">
                                    <div className="relative w-36">
                                      <select
                                        data-testid="winner-status-select"
                                        value={w.status}
                                        onChange={(e) => setStatus(d.date, w.entry_id, e.target.value)}
                                        className="h-9 w-full appearance-none rounded-lg border border-line bg-white px-2 text-xs text-ink focus:border-gold focus:outline-none"
                                      >
                                        {WINNER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                      </select>
                                      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-mutedlight" />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formErrors && (
              <div className="rounded-[16px] border border-line bg-white p-6" data-testid="admin-form-errors-panel">
                <h2 className="font-display text-lg font-extrabold text-ink">Formularfehler</h2>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="form-error-counts">
                  {Object.entries(formErrors.counts || {}).map(([code, n]) => (
                    <span key={code} className="rounded-full bg-offwhite px-3 py-1 text-xs font-medium text-coal">{code}: {n}</span>
                  ))}
                  {Object.keys(formErrors.counts || {}).length === 0 && (
                    <span className="text-sm text-mutedlight">Noch keine Fehler protokolliert.</span>
                  )}
                </div>
                {formErrors.errors?.length > 0 && (
                  <ul className="mt-4 max-h-56 space-y-1.5 overflow-y-auto text-xs text-mutedlight" data-testid="form-error-list">
                    {formErrors.errors.map((e, i) => (
                      <li key={i} className="flex justify-between gap-4 rounded-lg bg-offwhite px-3 py-2">
                        <span className="font-medium text-coal">{e.code}</span>
                        <span>{e.device || "–"} · {e.browser || "–"} · {e.ts ? new Date(e.ts).toLocaleString("de-CH") : ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="rounded-[16px] border border-line bg-white p-6" data-testid="admin-entries-panel">
              <div className="flex flex-col gap-3 md:flex-row">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mutedlight" />
                  <input
                    data-testid="admin-search-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Suche nach Name, E-Mail oder Telefon"
                    className={`${inputCls} pl-9`}
                  />
                </div>
                <div className="relative md:w-60">
                  <select data-testid="admin-consent-filter" value={consent} onChange={(e) => setConsent(e.target.value)} className={selectCls}>
                    <option value="">Alle Einwilligungen</option>
                    <option value="marketing">Mit Marketing-Opt-in</option>
                    <option value="newsletter">Mit Newsletter-Opt-in</option>
                    <option value="none">Ohne Opt-ins</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mutedlight" />
                </div>
                <button
                  data-testid="admin-refresh-button"
                  onClick={() => loadAll(token)}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl border border-line px-4 text-sm text-ink transition-colors hover:border-gold"
                >
                  <RefreshCw className="h-4 w-4" /> Aktualisieren
                </button>
              </div>
              <div className="mt-5 overflow-x-auto" data-testid="admin-entries-table-wrapper">
                <table className="w-full min-w-[720px] text-left text-sm" data-testid="admin-entries-table">
                  <thead>
                    <tr className="border-b border-line text-xs uppercase tracking-wider text-mutedlight">
                      <th className="py-3 pr-4">Datum</th>
                      <th className="py-3 pr-4">Name</th>
                      <th className="py-3 pr-4">E-Mail</th>
                      <th className="py-3 pr-4">Telefon</th>
                      <th className="py-3 pr-4">Marketing</th>
                      <th className="py-3 pr-4">Newsletter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr><td colSpan="6" className="py-8 text-center text-mutedlight" data-testid="admin-entries-empty">Keine Teilnahmen gefunden.</td></tr>
                    ) : entries.map((e) => (
                      <tr key={e.id || e.email} className="border-b border-line/60 text-coal" data-testid="admin-entry-row">
                        <td className="py-3 pr-4 whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleDateString("de-CH") : "–"}</td>
                        <td className="py-3 pr-4">{e.first_name} {e.last_name}</td>
                        <td className="py-3 pr-4">{e.email}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">{e.phone}</td>
                        <td className="py-3 pr-4">{e.fianza_marketing_consent || e.marketing_consent ? <span className="font-semibold text-gold-dark">ja</span> : "nein"}</td>
                        <td className="py-3 pr-4">{e.chickenwinner_newsletter_consent ? <span className="font-semibold text-gold-dark">ja</span> : "nein"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                data-testid="admin-download-csv-button"
                onClick={() => downloadCsv(token, "all")}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-[14px] bg-gold font-display text-lg font-extrabold text-ink transition-colors duration-200 hover:bg-gold-dark"
              >
                <Download className="h-5 w-5" strokeWidth={2} /> Alle Teilnahmen (CSV)
              </button>
              <button
                data-testid="admin-download-partner-csv-button"
                onClick={() => downloadCsv(token, "partner")}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-[14px] border border-gold/60 font-display text-lg font-extrabold text-gold-dark transition-colors duration-200 hover:bg-gold-dim"
              >
                <Download className="h-5 w-5" strokeWidth={2} /> Partner-Leads (CSV)
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
